<template>
  <div
    class="login-page-fix"
    style="background-color: white"
    :style="`${IS_DOH_THEME ? `height: ${getScreenSize.height - 180}px` : ''}`"
  >
    <!-- Deactivated org block — candidate must switch, cannot dismiss -->
    <CModal
      color="danger"
      :show.sync="showDeactivatedModal"
      :closeOnBackdrop="false"
      centered
    >
      <template #header>
        <div class="d-flex align-items-center">
          <i class="fa-solid fa-circle-xmark mr-2 text-white" style="font-size:1.1rem"></i>
          <h5 class="modal-title text-white mb-0">Organisation Account Deactivated</h5>
        </div>
      </template>
      <div class="px-3 py-3">
        <div class="deact-banner mb-3">
          <i class="fa-solid fa-triangle-exclamation mr-2"></i>
          Access to this organisation is no longer available.
        </div>
        <p style="font-size:0.9rem;color:#2c3e50" class="mb-3">
          The organisation account you are trying to access has been deactivated.
          You are not permitted to log in to this organisation.
        </p>
        <div v-if="orgDeactivated && orgDeactivated.primary_path_acronym" class="deact-primary-box mb-3">
          <i class="fa-solid fa-building-circle-check mr-2"></i>
          You can switch to your primary organisation to continue using TalentFind.
        </div>
        <div class="deact-support-box">
          <i class="fa-solid fa-headset mr-2"></i>
          If you believe this is an error or need to reactivate this organisation,
          please contact the TalentFind support team.
        </div>
      </div>
      <template #footer>
        <CButton
          v-if="orgDeactivated && orgDeactivated.primary_path_acronym"
          color="primary"
          @click="switchToPrimaryOrgLogin"
        >
          <i class="fa-solid fa-right-left mr-1"></i>
          Switch to Primary Organisation
        </CButton>
        <CButton v-else color="secondary" disabled>
          <i class="fa-solid fa-headset mr-1"></i> Contact Support
        </CButton>
      </template>
    </CModal>

    <CModal
      :show.sync="showDualLoginModal"
      :closeOnBackdrop="false"
      centered
      size="sm"
    >
      <template #header>
        <h5 class="mb-0">Active Session Detected</h5>
      </template>
      <div class="p-2">
        You are already logged in on <strong>{{ existingDevice }}</strong>. Do you want to logout that session?
      </div>
      <template #footer>
        <CButton color="secondary" @click="onCancelDualLogin">No</CButton>
        <CButton color="primary" @click="onConfirmDualLogin">Yes</CButton>
      </template>
    </CModal>

    <CModal
      :show="!!pendingReactivation"
      :closeOnBackdrop="false"
      centered
      size="sm"
    >
      <template #header>
        <h5 class="mb-0">Organisation Removed</h5>
      </template>
      <div class="p-2">
        {{ pendingReactivation && pendingReactivation.data.org_deactivated_message }}
      </div>
      <template #footer>
        <CButton color="secondary" @click="cancelReactivation">Cancel</CButton>
        <CButton color="primary" :disabled="isReactivating" @click="onConfirmReactivation">Activate</CButton>
      </template>
    </CModal>

    <LoadingOverlay v-if="isLoginFetching" />
    <div class="row login-page-container">
      <div :class="{ 'd-none': !showImage }" class="col-md-8 p-0">
        <img :src="getBanner" class="img-fluid" style="min-height:100vh;"/>
      </div>
      <div :class="{ 'col-md-12 col-sm-12 overflow-hidden': !showImage }" 
        class="col-md-4 p-0 login-form-container">
        <CContainer
          class="login-container px-0"
          style="min-height: calc(100vh - 95px)"
        >
          <CCardGroup
            class="d-flex card-group has-loading-overlay justify-content-center"
          >
            <CCard class="sign-in-card p-4 border-0">
              <CCardBody>
                <CForm @submit.prevent="loginSubmit">
                  <div v-if="IS_DOH_THEME" style="text-align: center">
                    <img
                      style="width: 85%"
                      :src="getHeaderLogo"
                      @error="$event.target.src = '/img/doh.png'"
                      alt="logo"
                    />
                  </div>
                  <img
                    id="loginLogo"
                    v-else
                    style="width: 100%"
                    :src="getLoginLogo"
                    @error="$event.target.src = '/img/new_tf_logo.svg'"
                    alt="logo"
                  />
                  <div class="sign-title mt-5 text-center">
                    <strong>Sign in to </strong>
                    <strong v-if="IS_DOH_THEME">your account</strong>
                    <span v-if="isAcronymBasedLogin && !IS_DOH_THEME && getCustomerLogo && !customer_logo.error">
                      <img
                        :src="getCustomerLogo"
                        @error="handleImageError"
                        @load="customer_logo.image_loaded = true"
                        :style="{ 
                          marginBottom: '3px', 
                          width: '130px',
                          opacity: customer_logo.image_loaded ? 1 : 0,
                          transition: 'opacity 0.2s',
                          display: 'inline-block',
                          minHeight: '30px'
                        }"
                      />
                    </span>
                    <span v-else-if="!customer_logo.image_loaded && isAcronymBasedLogin && !IS_DOH_THEME && getCustomerLogo && !customer_logo.error">
                      <!-- Empty span while image is loading -->
                    </span>
                    <span v-else>
                      <span style="color:#dc0050">
                        <strong>TALENT</strong>FIND
                        <strong style="vertical-align:super;font-size:19px">TM</strong>
                      </span>
                    </span>
                    <!-- <span
                      v-if="!isAcronymBasedLogin && !IS_DOH_THEME"
                      style="color: #dc0050"
                    >
                      <strong>TALENT</strong>FIND<strong
                        style="vertical-align: super; font-size: 13px"
                        >TM</strong
                      >
                    </span> -->
                  </div>
                  <CRow class="mt-3">
                    <label class="ml-3">Email Address</label>
                    <CCol class="text-right">
                      <span
                        class="login-hint text-primary"
                        v-c-tooltip="'Your email is your username!'"
                        >Login Hint</span
                      >
                    </CCol>
                  </CRow>
                  <CInput
                    name="login-email"
                    autocomplete="username email"
                    v-model="username"
                    tabindex="1"
                  ></CInput>
                  <CRow>
                    <label class="ml-3 pt-2">Password</label>
                    <CCol class="text-right">
                      <CButton
                        @click="redirectToForgot"
                        color="link"
                        class="px-0"
                        >Forgot password?</CButton
                      >
                    </CCol>
                  </CRow>
                  <CInput
                    name="login-password"
                    :type="showPassword ? 'text' : 'password'"
                    v-model="password"
                    autocomplete="new-password"
                    tabindex="2"
                  >
                    <template #append>
                      <span class="input-group-text login-password-toggle" @click="showPassword = !showPassword">
                        <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                      </span>
                    </template>
                  </CInput>
                  <div v-if="!getOrgAcronym">
                    <CButton
                      class="ml-5"
                      color="link"
                      @click="routeToListOrganisation"
                    >
                      Login as Facility/Candidate
                    </CButton>
                  </div>
                  <CRow class="justify-content-end mb-4">
                    <CButton
                      name="login-submit"
                      :disabled="isLoginFetching"
                      type="submit"
                      class="px-4 mr-3 btn-primary"
                      >{{
                        !isLoginFetching ? "Sign in" : "Loading..."
                      }}</CButton
                    >
                  </CRow>
                  <div
                    v-if="isAcronymBasedLogin"
                    class="d-flex justify-content-center"
                  >
                    <p style="font-size: 10px" class="p-0">
                      Click here to
                      <CButton
                        @click="redirectToUnsubsribe"
                        color="link"
                        class="px-0"
                        style="font-size: 10px"
                        >unsubscribe</CButton
                      >
                    </p>
                  </div>
                  <div
                    class="d-flex justify-content-center"
                    v-if="isAcronymBasedLogin"
                  >
                    <CButton
                      v-if="!isEducationalInstitute"
                      name="view-and-apply-jobs"
                      color="link"
                      class="font-weight-bold active px-0 fs-15 text-primary"
                      @click="redirectToPublicJobBoard"
                      >View & Apply Jobs<span class="small-text mx-1"
                        ><sup>New</sup></span
                      ></CButton
                    >
                  </div>
                </CForm>
              </CCardBody>
            </CCard>
          </CCardGroup>
        </CContainer>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  Role,
  CANDIDATE_URL,
  FACILITY_CANDIDATE_URL,
  LOGIN_URL,
  IS_DOH_THEME,
  getLocalOrgDetail,
  CONSTANT as CONST,
  generateLocalFileUrl,
  candidateLandingPath
} from "@/helpers/helper";

export default {
  name: "Login",
  data() {
    return {
      Role,
      CANDIDATE_URL,
      FACILITY_CANDIDATE_URL,
      IS_DOH_THEME,
      getLocalOrgDetail,
      username: "",
      password: "",
      showPassword: false,
      getOrgAcronym: "",
      showImage: true,
      generateLocalFileUrl,
      customer_logo:{
        error: false,
        image_loaded: false
      },
      showDualLoginModal: false,
      showDeactivatedModal: false,
      isReactivating: false,
      existingDevice: "",
    };
  },
  computed: {
    ...mapGetters([
      "getUserData",
      "isLoginFetching",
      "getRoleScopes",
      "getImages",
      "isAcronymBasedLogin",
      "getPathAcronymByOrgDetail",
      "isEducationalInstitute",
      "orgDeactivated",
      "pendingReactivation",
    ]),
    getBanner() {
      return generateLocalFileUrl(this.getImages?.banner);
    },
    getCustomerLogo() {
      return generateLocalFileUrl(this.getImages?.customer_logo);
    },
    getHeaderLogo() {
      return generateLocalFileUrl(this.getImages?.header_logo);
    },
    getLoginLogo() {
      return generateLocalFileUrl(this.getImages?.login_logo);
    },
    getScreenSize() {
      return {
        width: screen.availWidth,
        height: screen.availHeight,
      };
    },
  },
  mounted() {
    if(window.innerWidth <= 800) this.showImage = false;
    const {
      params: { org_name: pid },
    } = this.$router.currentRoute;
    this.getOrgAcronym = pid || "";
    if (this.getRoleScopes.length) {
      if (
        this.getRoleScopes[0] === Role.systemCandidate ||
        this.getRoleScopes[0] === Role.customerCandidate
      ) {
        this.$router.push(candidateLandingPath(this.$route.query));
      } else if (this.getRoleScopes[0] === Role.systemAdmin) {
        this.$router.push(this.CANDIDATE_URL);
      } else {
        this.$router.push(this.FACILITY_CANDIDATE_URL);
      }
    }
    this.routeToTalentFind();
  },
  methods: {
    ...mapActions([
      "login",
      "showToast",
      "resetCandidate",
      "isEmailBelongsToAcronym",
      "fetchOrgDetailsByAcronym",
      "confirmDualLogin",
      "cancelDualLogin",
      "confirmReactivation",
      "cancelReactivation",
    ]),
    loginSubmit() {
      this.username = this.username.toLowerCase().trim();
      this.password = this.password.trim();
      const login_data = {
        username: this.username,
        password: this.password
      }
      if (this.username && this.password) {
        this.isEmailBelongsToAcronym(login_data)
          .then(async (res) => {
            const path_acronym = res?.data?.detail?.path_acronym || "";
            if (res?.status === 200) {
              if(path_acronym)  {
                localStorage.setItem("logoutPathAcronym", path_acronym);
                await this.fetchOrgDetailsByAcronym(path_acronym)
              }
              const loginRes = await this.login(login_data);
              if (loginRes?.has_existing_session) {
                this.existingDevice = loginRes.existing_device || "Another Device";
                this.showDualLoginModal = true;
              }
            }
          })
      } else {
        this.showToast({
          class: "bg-danger text-white",
          message: "Please fill the credentials",
        });
      }
    },
    async onConfirmDualLogin() {
      this.showDualLoginModal = false;
      await this.confirmDualLogin();
    },
    onCancelDualLogin() {
      this.showDualLoginModal = false;
      this.cancelDualLogin();
    },
    async onConfirmReactivation() {
      this.isReactivating = true;
      try {
        await this.confirmReactivation();
      } finally {
        this.isReactivating = false;
      }
    },
    redirectToForgot() {
      this.$router.push({
        path: `${
          this.isAcronymBasedLogin ? `${LOGIN_URL()}/forgot` : "/forgot"
        }`,
      });
    },
    redirectToUnsubsribe() {
      this.$router.push({ path: LOGIN_URL() + "/unsubscribe" });
    },
    redirectToPublicJobBoard() {
      let path = ""
      if (getLocalOrgDetail()?.customer_type_id === CONST.CUSTOMER_TYPES.educational_institute) {
        path = `/public/job-board/${CONST.CUSTOMERS.super_customer}`
      }
      else {
        path = `/public/job-board/${getLocalOrgDetail()?.customer_uid}`
        // ${
        //   getLocalOrgDetail()?.organisation_id
        //     ? `/org/${getLocalOrgDetail()?.organisation_id}`
        //     : ""
        // }`     
      }
      this.$router.push({ path });
    },
    routeToListOrganisation() {
      this.$router.push({ name: "ListOrganisation" });
    },
    routeToTalentFind() {
      if (window.location.host === "talentfind.com") {
        this.$router.push({
          path: `${"/tfm"}`,
        });
      }
    },
    switchToPrimaryOrgLogin() {
      const acronym = this.orgDeactivated?.primary_path_acronym;
      if (acronym) {
        this.showDeactivatedModal = false;
        this.$store.commit("SET_ORG_DEACTIVATED", null);
        window.location.href = `/${acronym}`;
      }
    },
    handleImageError() {
      this.customer_logo.error = true;
      this.customer_logo.image_loaded = false;
    }
  },
  watch: {
    orgDeactivated(val) {
      this.showDeactivatedModal = !!(val);
    },
    "$route.name": function () {
      this.routeToTalentFind();
      this.page = this.$router.currentRoute.name;
      const {
        params: { org_name: pid },
      } = this.$router.currentRoute;
      this.getOrgAcronym = pid || "";
    },
    getCustomerLogo(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.customer_logo.error = false;
        this.customer_logo.image_loaded = false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.deact-banner {
  background: #fff0f0;
  border-left: 4px solid #e55353;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 0.875rem;
  color: #c0392b;
  font-weight: 600;
}
.deact-primary-box {
  background: #f0f7ff;
  border-left: 4px solid #321fdb;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 0.875rem;
  color: #2c3e50;
}
.deact-support-box {
  background: #fff8e1;
  border-left: 4px solid #ffc107;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 0.82rem;
  color: #856404;
}
@media (max-width: 1000px) {
  #bg-img {
    display: none;
  }
}
.login-container {
  .card-group {
    .sign-in-card {
      max-width: 400px;
      @media (min-width: 1000px) {
        margin-top: 50px;
      }
      .sign-title {
        font-size: 22px;
        line-height: 28px;
      }
    }
  }
}
.login-password-toggle {
  cursor: pointer;
}
.login-hint {
  font-size: 0.65rem;
  color: #dd3651;
  font-weight: 400;
  cursor: pointer;
}
.login-page-fix {
  margin-top: -22px;
  overflow-x: hidden;
}

@media (min-width: 360px) {
  .login-page-fix {
    margin-right: -30px;
    margin-left: -30px;
  }
}

.login-page-container {
  background-size: contain;
  background-repeat: no-repeat;
  -moz-background-size: contain;
  -ms-background-size: contain;
  -o-background-size: contain;
  -webkit-background-size: contain;
  background-size: contain;

  .bg-img {
    width: auto;
    height: 100%;
    object-fit: cover;
  }
}
</style>
